import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    Button,
    Box,
    Typography,
    TextField,
    CircularProgress,
    Alert,
} from '@mui/material';
import { Storefront as StorefrontIcon } from '@mui/icons-material';
import DemandService from '../services/DemandService';

/**
 * PickupPackageModal - число мест для доставки без ТК (самовывоз,
 * «Оформление доставки клиентом»).
 *
 * В отличие от PackageModal: нет СДЭК/Яндекса, грузомест и печати наклеек.
 * Введённое число уходит на POST /demand/:id/pickup-package, бэкенд дописывает
 * его в description отгрузки («[УПАКОВКА дата] <способ доставки>, мест: N») и
 * уведомляет чат сборки в Bitrix24.
 *
 * Props:
 *   open: boolean              - состояние открытия
 *   onClose: () => void        - закрыть модалку (также вызывается при Cancel)
 *   demandId: string           - UUID demand (отгрузки)
 *   demandName: string         - имя/номер отгрузки для отображения
 *   deliveryMethod: string     - название способа доставки для заголовка
 *   onSuccess: (data) => void  - callback успеха, data = { ok, packagesCount }
 *
 * Cancel/ESC - soft-cancel: статус demand уже «Упаковано» (changeStatus
 * отработал до открытия модалки), отгрузка остаётся в работе у упаковщика.
 * Модалка откроется повторно при заходе в заказ.
 */
export default function PickupPackageModal({ open, onClose, demandId, demandName, deliveryMethod, onSuccess }) {
    const [packagesCount, setPackagesCount] = useState('1');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Сброс состояния при открытии. Дефолт 1: у самовывоза чаще одно место.
    useEffect(() => {
        if (open) {
            setPackagesCount('1');
            setLoading(false);
            setError(null);
        }
    }, [open]);

    const handleClose = () => {
        if (loading) return; // не закрывать во время запроса
        onClose();
    };

    const handleSubmit = async (e) => {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }
        const n = parseInt(packagesCount, 10);
        if (!Number.isInteger(n) || n < 1) {
            setError('Введите целое число, большее или равное 1');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await DemandService.pickupPackage(demandId, n);
            const data = (response && response.data) ? response.data : response;
            onSuccess(data);
        } catch (err) {
            const status = err && err.response && err.response.status;
            const data = err && err.response && err.response.data;
            if (status === 404) {
                setError('Отгрузка не найдена');
            } else if (status === 409) {
                setError('У этой отгрузки есть трек-номер - она едет через ТК. Закройте окно и обратитесь к менеджеру.');
            } else {
                setError('Ошибка: ' + ((data && data.message) || (err && err.message) || 'неизвестная'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Валидность ввода: целое число >= 1
    const parsed = parseInt(packagesCount, 10);
    const isValid = Number.isInteger(parsed) && parsed >= 1;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            disableEscapeKeyDown={loading}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorefrontIcon color="primary" />
                <Typography variant="h6" component="span">
                    {deliveryMethod ? `${deliveryMethod}: количество мест` : 'Количество мест'}
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    {demandName && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Отгрузка: <strong>{demandName}</strong>
                        </Typography>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Транспортная компания не нужна. Число мест запишется
                        в комментарий отгрузки.
                    </Typography>

                    <form onSubmit={handleSubmit} id="pickup-package-modal-form">
                        <TextField
                            label="Сколько мест?"
                            type="number"
                            inputProps={{ min: 1, step: 1 }}
                            value={packagesCount}
                            onChange={(e) => setPackagesCount(e.target.value)}
                            disabled={loading}
                            autoFocus
                            fullWidth
                            error={Boolean(packagesCount) && !isValid}
                            helperText={
                                !packagesCount
                                    ? 'Введите целое число, большее или равное 1'
                                    : !isValid
                                        ? 'Значение должно быть целым числом >= 1'
                                        : ' '
                            }
                        />
                    </form>
                </Box>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mt: 1 }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, color: 'primary.main' }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Записываем...</Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    color="inherit"
                    variant="outlined"
                >
                    Отмена
                </Button>

                <Button
                    type="submit"
                    form="pickup-package-modal-form"
                    onClick={handleSubmit}
                    disabled={!isValid || loading}
                    color="success"
                    variant="contained"
                >
                    {loading ? 'Выполняется...' : 'Подтвердить'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
